import React from 'react'
import PropTypes from 'prop-types'
import styled, { css } from 'styled-components'
import Mixin from 'components/common/Mixin'
import Media from 'components/common/Media'
import Theme from 'components/common/Theme/Theme'
import Button from 'components/common/Button/Button'
import { A } from 'components/common/Link/Link'
import Badge from 'components/common/Badge/Badge'
import Columns from 'components/common/Columns/Columns'
import UpdatingBlock from 'components/common/UpdatingBlock'
import { ADDING_TO_SHOPPING_QUOTE } from 'components/common/UpdatingBlock/constants'
import { MobileButtonWithAddToQuoteHandler } from '../../../components/AddToQuoteButton'
import QuantityPrice from '../../../components/QuantityPrice'
import ProductHeader from '../ProductHeader'
import { RECOMMENDED_PRODUCTS_MINIMUM_COUNT } from '../../constants'

const RecommendedProduct = ({
  bvId,
  countFront,
  clear,
  countRear,
  avgRating,
  brandLogoUrl,
  brandName,
  expanderMenu,
  productDisplayName,
  quantityInputId,
  title,
  toolTipMessage,
  price,
  skuDisplayName,
  imagePath,
  warranty,
  compareDisabled,
  handleCompareTrayAdd,
  skuId,
  defaultQuantityValue = 0,
  skuPromotions = [],
  handleFindLowerPriceClick,
  myStore,
  showStoreLocatorModal,
  sizeFront,
  sizeRear,
  specifications,
  promotions,
  tooltip,
  recommendedCount,
  reviewsLink,
  tireDetailUrl,
  handleProductClick,
  handleFrontQuantityChange,
  handleRearQuantityChange,
  warrantyBadges,
  disableAddToQuote,
  zeroPriceItemMessage,
  isOeItem,
  isEvItem,
  itemEVType,
  isPilotStore,
  inventoryStatuses,
  availability,
  compatibilityState,
  vehicleName
}) =>
  <React.Fragment>
    <Badge
      empty
      productBadge
      category={ title }
      tooltip={ tooltip } />
    <Product data-tire-gbb>
      <ProductHeader
        availability={ availability }
        storeNumber={ myStore.storeNumber }
        inventoryStatuses={ inventoryStatuses }
        isPilotStore={ isPilotStore }
        compatibilityState={ compatibilityState }
        vehicleName={ vehicleName }
        skuId={ skuId }
        bvId={ bvId }
        brandName={ brandName }
        recommendedCount={ recommendedCount }
        srcTire={ imagePath }
        srcLogo={ brandLogoUrl }
        reviewsLink={ reviewsLink }
        tireDetailUrl={ tireDetailUrl }
        title={ productDisplayName }
        sizeFront={ sizeFront }
        ratingCount={ avgRating }
        warranty={ warranty }
        warrantyBadges={ warrantyBadges }
        specifications={ specifications }
        promotions={ promotions }
        handleProductClick={ handleProductClick }
        expanderMenu={ expanderMenu }
        countFront={ countFront }
        countRear={ countRear }
        isOeItem={ isOeItem }
        itemEVType={ itemEVType }
        isEvItem={ isEvItem } />
      <Wrapper recommendedCount={ recommendedCount } name={ ADDING_TO_SHOPPING_QUOTE } value={ skuId } >
        <QuantityPrice
          quantityInputId={ quantityInputId }
          dataAttributes={{ 'data-at-serp-gbb-price': true }}
          priceTitle={ sizeRear ? 'Front' : '' }
          defaultValue={ countFront }
          showStoreLocatorModal={ showStoreLocatorModal }
          handleQuantityChange={ handleFrontQuantityChange }
          zeroPriceItemMessage={ zeroPriceItemMessage }
          priceOld={ price }
          myStore={ myStore }
          count={ countFront }
          hidePriceOfSet />
        <ButtonColumns width="50% 50%">
          <CustomButton
            data-at-compare-button
            full
            secondary
            narrow
            disabled={ compareDisabled }
            onClick={ handleCompareTrayAdd }>
            + Compare
          </CustomButton>
          <MobileButtonWithAddToQuoteHandler
            full
            disabled={ disableAddToQuote }
            productInfo={{ skuId, quantity: countFront, label: title, oeFlag: isOeItem, evFlag: isEvItem }} />
        </ButtonColumns>
        {
          myStore &&
          <CustomLink
            data-at-recommended-product-find-lower-price
            bolder
            prevent
            handler={ handleFindLowerPriceClick }>
            Found a lower price? Let us know.
          </CustomLink>
        }
      </Wrapper>
    </Product>
  </React.Fragment>

RecommendedProduct.propTypes = {
  availability: PropTypes.shape(),
  avgRating: PropTypes.number,
  brandLogoUrl: PropTypes.string,
  brandName: PropTypes.string,
  bvId: PropTypes.string,
  clear: PropTypes.bool,
  compareDisabled: PropTypes.bool,
  compatibilityState: PropTypes.string,
  countFront: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number
  ]),
  countRear: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number
  ]),
  defaultQuantityValue: PropTypes.number,
  disableAddToQuote: PropTypes.bool,
  expanderMenu: PropTypes.arrayOf(PropTypes.shape()),
  handleCompareTrayAdd: PropTypes.func,
  handleFindLowerPriceClick: PropTypes.func,
  handleFrontQuantityChange: PropTypes.func,
  handleProductClick: PropTypes.func,
  handleRearQuantityChange: PropTypes.func,
  imagePath: PropTypes.string,
  inventoryStatuses: PropTypes.arrayOf(PropTypes.shape()),
  isEvItem: PropTypes.bool,
  isOeItem: PropTypes.bool,
  isPilotStore: PropTypes.bool,
  itemEVType: PropTypes.string,
  myStore: PropTypes.shape(),
  price: PropTypes.number,
  productDisplayName: PropTypes.string,
  promotions: PropTypes.arrayOf(PropTypes.shape()),
  quantityInputId: PropTypes.string,
  recommendedCount: PropTypes.number,
  reviewsLink: PropTypes.string,
  showStoreLocatorModal: PropTypes.func,
  sizeFront: PropTypes.string,
  sizeRear: PropTypes.string,
  skuDisplayName: PropTypes.string,
  skuId: PropTypes.string,
  skuPromotions: PropTypes.arrayOf(PropTypes.shape()),
  specifications: PropTypes.arrayOf(PropTypes.shape()),
  tireDetailUrl: PropTypes.string,
  title: PropTypes.string,
  tooltip: PropTypes.string,
  toolTipMessage: PropTypes.shape(),
  vehicleName: PropTypes.string,
  warranty: PropTypes.string,
  warrantyBadges: PropTypes.arrayOf(PropTypes.shape()),
  zeroPriceItemMessage: PropTypes.string
}

export default RecommendedProduct

const Product = styled.div`
  background-color: #f6f6f6;
  box-shadow: 1px 1px 4px ${Theme.colors.transparent_overlay_shadow};
  height: 100%;
`

const Wrapper = styled(UpdatingBlock)`
  background-color: ${Theme.colors.off_white};
  border-top: 1px solid ${Theme.colors.light_grey};
  ${Mixin.responsive('padding', '15px 20px', '10px 20px 15px')}
  ${props => props.recommendedCount > RECOMMENDED_PRODUCTS_MINIMUM_COUNT && css`
    ${Media.tablet`
      padding: 15px 10px;
    `}
  `}
`

const CustomLink = styled(A)`
  display: inline-block;
  font-size: 12px;
  line-height: 17px;
  ${Mixin.responsive('margin', '20px 0 0', '17px 0 0')}
`

const CustomButton = styled(Button)`
  ${Mixin.responsive('padding', '11px 5px 9px', '11px 12px 9px')}
`

const ButtonColumns = styled(Columns)`
  [data-column]:first-child {
    padding-right: 5px;
  }

  [data-column]:last-child {
    padding-left: 5px;
  }
`
